"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertTriangle,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEmailAgents, type EmailAgent, type EmailAgentConfig } from "@/hooks/agents/email";
import {
  useEmailClassifierConfigQuery,
  emailClassifierConfigQueryKey,
} from "@/hooks/queries/email-classifier-config";
import { useUpdateEmailClassifierConfigMutation } from "@/hooks/mutations/email-classifier-config";
import { RoleMultiSelect } from "@/components/agents/shared/role-multi-select";
import { ComposedPromptDialog } from "@/components/agents/shared/composed-prompt-dialog";
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose";
import { RoleChangeConfirmDialog } from "@/components/agents/shared/role-change-confirm-dialog";
import { ResetToRoleDefaultButton } from "@/components/agents/shared/reset-to-role-default-button";
import { PromptHistoryButton } from "@/components/agents/shared/prompt-history-button";
import { AGENT_PRIMARY_ROLE_USE_CASE, DEFAULT_ROLE_SLUG } from "@/lib/ai/roles/types";
import { AI_USE_CASES } from "@/lib/ai/langchain/types";
import type { AiUseCaseRoleView } from "@/server-actions/ai-use-case-roles";
import { PageLoader } from "@/components/ui/page-loader";
import { AutoReplySettings } from "./auto-reply-settings";
import { AiDisclosureSettings } from "@/components/organization/ai-disclosure-settings";

interface GeneralSettingsProps {
  agent: EmailAgent | null;
  isCreating: boolean;
  onSave: (agent: EmailAgent) => void;
  onSavingChange: (isSaving: boolean) => void;
}

const STATUS_TRIGGER_STYLES = {
  draft: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
  active: "border-green-300 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-950/40 dark:text-green-200",
  disabled: "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200",
} as const;

export function GeneralSettings({
  agent,
  isCreating,
  onSave,
  onSavingChange,
}: GeneralSettingsProps) {
  const { createAgent, updateAgent } = useEmailAgents();

  // Basic info state
  const [name, setName] = useState(agent?.name || "");
  const [status, setStatus] = useState<"draft" | "active" | "disabled">(agent?.status || "draft");
  const [roles, setRoles] = useState<string[]>(agent?.config?.roles ?? [DEFAULT_ROLE_SLUG]);
  const [roleLabel, setRoleLabel] = useState<string>(agent?.config?.role_label ?? "");
  const [replyConfig, setReplyConfig] = useState<EmailAgentConfig>(agent?.config ?? {});
  // Held until the user confirms the preset overwrite.
  const [pendingRoles, setPendingRoles] = useState<{ slugs: string[]; names: string[] } | null>(null);
  const [previewRoles, setPreviewRoles] = useState<string[] | null>(null);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const queryClient = useQueryClient();

  // Check if agent has inboxes connected (for status validation)
  const hasInboxes = (agent?.inboxes?.length ?? 0) > 0;
  const [error, setError] = useState<string | null>(null);
  const [isSavingBasic, setIsSavingBasic] = useState(false);

  // Classifier config lives on the scoped AI-config chain, not in agent.config.
  const { data: classifierConfig, isLoading: isLoadingClassifier } =
    useEmailClassifierConfigQuery(agent?.id || null);
  const updateClassifierMutation = useUpdateEmailClassifierConfigMutation(agent?.id || null);

  // Classification state
  const [labels, setLabels] = useState("");
  const [classifierPrompt, setClassifierPrompt] = useState("");

  useEffect(() => {
    if (agent) {
      setName(agent.name);
      setStatus(agent.status);
      setRoles(agent.config?.roles ?? [DEFAULT_ROLE_SLUG]);
      setRoleLabel(agent.config?.role_label ?? "");
      setReplyConfig(agent.config ?? {});
    }
  }, [agent]);

  useEffect(() => {
    if (classifierConfig) {
      setLabels(classifierConfig.classifier_labels.join(", "));
      setClassifierPrompt(classifierConfig.system_prompt);
    }
  }, [classifierConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    // Validate status change - can't activate without inboxes
    if (!isCreating && status === "active" && !hasInboxes) {
      setError("Cannot activate agent without at least one inbox connected. Go to the Inbox tab to add inboxes.");
      return;
    }

    onSavingChange(true);
    setIsSavingBasic(true);

    try {
      if (isCreating) {
        // Several roles are merged by AI — the user reviews that text before it
        // is applied (and the same generation is what gets saved). A single role
        // is applied verbatim, so it needs no review.
        if (roles.length > 1) {
          setPreviewRoles(roles);
          return;
        }
        const newAgent = await createAgent({
          name: name.trim(),
          roles,
          role_label: roleLabel.trim() || null,
        });
        onSave(newAgent);
      } else if (agent) {
        const updatedAgent = await updateAgent(agent.id, {
          name: name.trim(),
          status,
        });
        onSave(updatedAgent);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save agent");
    } finally {
      onSavingChange(false);
      setIsSavingBasic(false);
    }
  };

  const applyRoleUpdate = async (
    nextRoles: string[],
    applyPreset: boolean,
    composed?: ComposedRoleBundle,
  ) => {
    if (!agent) return;
    setError(null);
    setIsChangingRole(true);
    try {
      const updatedAgent = await updateAgent(agent.id, {
        roles: nextRoles,
        apply_preset: applyPreset,
        composed,
      });
      setRoles(nextRoles);
      onSave(updatedAgent);
      // Role apply rewrote the scoped classifier config — refetch it.
      await queryClient.invalidateQueries({
        queryKey: emailClassifierConfigQueryKey(agent.id),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change role");
      // Rethrow: the preview dialog must stay open and keep the composition it
      // already paid for, so a transient failure costs a retry, not a re-generation.
      throw err;
    } finally {
      setIsChangingRole(false);
    }
  };

  const handleRolesChange = (nextRoles: string[], roleData: AiUseCaseRoleView[]) => {
    if (isCreating) {
      setRoles(nextRoles);
      return;
    }
    // Slugs come from the selection, names only from the loaded presets — a role
    // deleted platform-side must not silently shrink the set on confirm.
    setPendingRoles({ slugs: nextRoles, names: nextRoles.map((slug) => roleData.find((r) => r.slug === slug)?.name ?? slug) });
  };

  const handleSaveClassifier = async () => {
    if (!agent) return;
    const classifierLabels = labels
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!classifierLabels.length || !classifierPrompt.trim()) {
      setError("Classification prompt and at least one category are required");
      return;
    }
    setError(null);
    try {
      await updateClassifierMutation.mutateAsync({
        system_prompt: classifierPrompt.trim(),
        classifier_labels: classifierLabels,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save classifier settings");
    }
  };

  // Show loader while config is loading (only for existing agents)
  if (!isCreating && isLoadingClassifier) {
    return <PageLoader message="Loading settings..." />;
  }

  const deliveryNotice = getDeliveryNotice(replyConfig, classifierConfig?.classifier_labels ?? []);

  return (
    <div className="space-y-6">
      <form id="agent-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Configure the basic settings for your email agent.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-x-6 gap-y-4 sm:grid-cols-[3fr_2fr] sm:items-start">
            <div className="space-y-2">
              <Label htmlFor="name">Agent Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Support Agent"
                required
              />
            </div>

            <div>
              <RoleMultiSelect
                useCase={AGENT_PRIMARY_ROLE_USE_CASE.email_agent}
                value={roles}
                onChange={handleRolesChange}
                roleLabel={roleLabel}
                onRoleLabelChange={setRoleLabel}
                onRoleLabelCommit={
                  agent
                    ? async (label) => {
                        onSave(await updateAgent(agent.id, { role_label: label || null }));
                      }
                    : undefined
                }
                loading={isChangingRole}
              />
            </div>

              {!isCreating && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as "draft" | "active" | "disabled")}>
                    <SelectTrigger id="status" className={STATUS_TRIGGER_STYLES[status]}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="active" disabled={!hasInboxes}>
                        <span>Active</span> <span>{!hasInboxes && "(requires inbox)"}</span>
                      </SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  {status !== "active" && (
                    <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300" role="status">
                      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                      <span>Agent is not active. Update the status to Active to start handling messages.</span>
                    </p>
                  )}
                  {status === "active" && agent?.status !== "active" && (
                    <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
                      Save Basic Settings to activate this agent.
                    </p>
                  )}
                  {!hasInboxes && (
                    <p className="text-sm text-yellow-600">
                      Connect at least one inbox in the Inbox tab to activate this agent.
                    </p>
                  )}
                </div>
              )}

              {!isCreating && deliveryNotice && (
                <div className="flex min-w-0 items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" role="status">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <div className="min-w-0 space-y-1">
                    <p>{deliveryNotice}</p>
                    <button type="button" className="font-medium underline underline-offset-2 hover:no-underline"
                      onClick={() => document.getElementById("email-category-settings")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                      Configure category settings
                    </button>
                  </div>
                </div>
              )}

              {!isCreating && (
                <div className="flex justify-end border-t pt-4 sm:col-span-2">
                  <Button type="submit" disabled={isSavingBasic} className="w-full sm:w-auto">
                    {isSavingBasic ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Basic Settings"
                    )}
                  </Button>
                </div>
              )}
          </CardContent>
        </Card>
      </form>

      <AiDisclosureSettings surface="email" />

      <RoleChangeConfirmDialog
        open={Boolean(pendingRoles)}
        onOpenChange={(open) => !open && setPendingRoles(null)}
        roleNames={pendingRoles?.names ?? []}
        overwrites={["system prompt", "classification settings"]}
        extraWarning="If the roles change the classification categories, Auto-Reply and CRM integration will be turned off and their category selections cleared — reconfigure them against the new categories before re-enabling."
        onConfirm={(applyPreset) => {
          const nextRoles = pendingRoles?.slugs ?? [];
          setPendingRoles(null);
          if (!nextRoles.length) return;
          if (applyPreset && nextRoles.length > 1) {
            setPreviewRoles(nextRoles);
            return;
          }
          // The error is already surfaced by applyRoleUpdate; nothing to retry here.
          void applyRoleUpdate(nextRoles, applyPreset).catch(() => {});
        }}
      />

      <ComposedPromptDialog
        open={Boolean(previewRoles)}
        onOpenChange={(open) => !open && setPreviewRoles(null)}
        scopeType="email_agent"
        roleSlugs={previewRoles ?? []}
        onApply={async (composed) => {
          const nextRoles = previewRoles ?? [];
          if (isCreating) {
            onSave(
              await createAgent({
                name: name.trim(),
                roles: nextRoles,
                role_label: roleLabel.trim() || null,
                composed,
              }),
            );
            return;
          }
          await applyRoleUpdate(nextRoles, true, composed);
        }}
      />

      {/* Classification Settings (only for existing agents) */}
      {!isCreating && agent && (
        <AutoReplySettings agent={agent} section="general" onDeliveryConfigChange={setReplyConfig} classificationSection={
        <Card>
          <CardHeader>
            <CardTitle>Category Classification</CardTitle>
            <CardDescription>
              <span>Configure how emails are classified by this agent.</span>
              <span>{classifierConfig?.source === "inherited" &&
                " Currently using tenant/global defaults — saving creates agent-specific settings."}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="labels" className="text-sm font-medium">
                <span>Classification Categories</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Comma-separated list of categories for email classification</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Input
                id="labels"
                placeholder="Qualified Lead, Support Request, Spam, Other"
                value={labels}
                onChange={(e) => setLabels(e.target.value)}
                className="text-sm"
              />
            </div>

            <div className="space-y-2">
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Label htmlFor="classifier-prompt" className="text-sm font-medium">
                <span>Classification Prompt</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>The system prompt used to classify emails</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>button]:whitespace-normal [&>button]:text-left [&>button]:[overflow-wrap:anywhere] [@media(width<640px)]:[&>button]:h-auto [@media(width<640px)]:[&>button]:min-h-10">
                <PromptHistoryButton
                  scopeType="email_agent"
                  scopeId={agent.id}
                  useCase={AI_USE_CASES.EMAIL_CLASSIFICATION}
                  currentPrompt={classifierPrompt}
                  onRestore={setClassifierPrompt}
                  canRestore
                />
                <ResetToRoleDefaultButton
                  scopeType="email_agent"
                  useCase={AI_USE_CASES.EMAIL_CLASSIFICATION}
                  roleSlugs={roles}
                  roleLabel={roleLabel}
                  disabled={updateClassifierMutation.isPending}
                  onReset={(composed) => {
                    setClassifierPrompt(composed.system_prompt);
                    const presetLabels = composed.settings.classifier_labels;
                    if (Array.isArray(presetLabels)) setLabels(presetLabels.join(", "));
                  }}
                />
              </div>
              </div>
              <Textarea
                id="classifier-prompt"
                placeholder="You are an expert email classifier..."
                value={classifierPrompt}
                onChange={(e) => setClassifierPrompt(e.target.value)}
                rows={8}
                className="text-sm"
              />
              <p className="text-xs text-gray-500">
                Full system prompt for classification. Categories are passed separately from the list above.
              </p>
            </div>

            <div className="flex justify-end border-t pt-4">
              <Button
                type="button"
                onClick={handleSaveClassifier}
                disabled={updateClassifierMutation.isPending}
                className="w-full sm:w-auto"
              >
                {updateClassifierMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Classification Settings"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        } />
      )}
    </div>
  );
}

function getDeliveryNotice(config: EmailAgentConfig, categories: string[]): string | null {
  const settings = config.auto_reply?.category_settings ?? {};
  const modes = categories
    .filter((category) => settings[category]?.enabled)
    .map((category) => {
      const categorySettings = settings[category];
      if (categorySettings.dry_run ?? true) return "dry_run";
      return categorySettings.create_draft_instead_of_send ? "draft" : "send";
    });

  if (modes.length === 0 || modes.every((mode) => mode === "send")) return null;
  if (modes.every((mode) => mode === "draft")) {
    return "Only saves replies as drafts; doesn't send them.";
  }
  if (modes.includes("draft") && modes.includes("send")) {
    return "Some categories save replies as drafts; others send them.";
  }
  if (modes.includes("draft")) {
    return "Some categories save replies as drafts; others run without sending.";
  }
  if (modes.includes("send")) {
    return "Some categories send replies; others run without sending.";
  }
  return "Only runs replies without sending or saving drafts.";
}
